import axios, { AxiosError } from 'axios'
import * as Sentry from '@sentry/react'

type LogErrorAttributes = Record<string, string | number>
type SafeJsonValue = string | number | boolean | null | undefined

export type AxiosRequestContext = {
	method: string
	baseURL?: string
	url: string
	fullUrl: string
	status: number
	requestHeaders?: Record<string, string>
	responseHeaders?: Record<string, string>
}

const SENSITIVE_HEADERS = ['authorization', 'cookie', 'set-cookie', 'x-user', 'x-auth']

/** Query param names whose values are redacted in Sentry axios context / logs. */
const SENSITIVE_QUERY_PARAMS = ['latmy', 'lonmy', 't', 'token']

const sanitizeHeaders = (headers: unknown): Record<string, string> | undefined => {
	if (!headers || typeof headers !== 'object') {
		return undefined
	}

	const out: Record<string, string> = {}

	Object.entries(headers as Record<string, unknown>).forEach(([key, value]) => {
		// Keep the key so we can see auth was present, but never leak the value.
		if (SENSITIVE_HEADERS.includes(key.toLowerCase())) {
			if (value === undefined || value === null || value === '') {
				return
			}
			out[key] = '[Filtered]'
			return
		}

		// Axios / node can store headers as string | string[] | number | boolean
		if (Array.isArray(value)) {
			out[key] = value.join(', ')
			return
		}
		out[key] = String(value as SafeJsonValue)
	})

	return Object.keys(out).length > 0 ? out : undefined
}

const serializeQueryParamValue = (value: unknown): string | undefined => {
	if (value === undefined || value === null) {
		return undefined
	}

	if (Array.isArray(value)) {
		return value.map((item) => String(item)).join(',')
	}

	return String(value as SafeJsonValue)
}

/**
 * Append axios `config.params` onto a URL, redacting sensitive keys.
 * Uses searchParams so encoding stays correct.
 */
const appendParams = (target: URL, params: unknown) => {
	if (!params || typeof params !== 'object') {
		return
	}

	Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
		const serialized = serializeQueryParamValue(value)
		if (serialized === undefined) {
			return
		}

		const isSensitive = SENSITIVE_QUERY_PARAMS.includes(key.toLowerCase())
		target.searchParams.set(key, isSensitive ? '[Filtered]' : serialized)
	})
}

/**
 * Join axios baseURL + url without dropping path segments of baseURL.
 * `new URL('/path', 'https://host/api')` would replace `/api` — we append instead.
 */
const buildFullUrl = (baseURL: string | undefined, url: string, params: unknown): string => {
	if (!url || url === 'UNKNOWN') {
		return 'UNKNOWN'
	}

	let resolved: URL

	try {
		if (/^https?:\/\//i.test(url)) {
			resolved = new URL(url)
		} else if (baseURL) {
			const base = baseURL.endsWith('/') ? baseURL : `${baseURL}/`
			const path = url.startsWith('/') ? url.slice(1) : url
			resolved = new URL(path, base)
		} else {
			resolved = new URL(url)
		}
	} catch {
		const joined = baseURL ? `${baseURL.replace(/\/$/, '')}/${url.replace(/^\//, '')}` : url
		return joined
	}

	appendParams(resolved, params)
	return resolved.toString()
}

export const getAxiosRequestContext = (error: AxiosError): AxiosRequestContext => {
	const method = error.config?.method?.toUpperCase() ?? 'UNKNOWN'
	const url = error.config?.url ?? 'UNKNOWN'
	const baseURL = error.config?.baseURL
	const status = error.response?.status ?? error.status ?? 0
	const fullUrl = buildFullUrl(baseURL, url, error.config?.params)

	return {
		method,
		baseURL,
		url,
		fullUrl,
		status,
		requestHeaders: sanitizeHeaders(error.config?.headers),
		responseHeaders: sanitizeHeaders(error.response?.headers)
	}
}

/**
 * Logs an error to Sentry and console with additional context attributes.
 * `Sentry.logger.error` explicitly logs the error to Sentry.
 * `console.error` logs the error to the console/stdout.
 * Because every `console.error` is by default wrapped and logged to Sentry, we use prefixing with CONSOLE and ignore those logs in Sentry.
 * @param error - The error to log (can be an Axios error or any unknown error)
 * @param identifier - A unique identifier for the error location/context
 * @param extraAttributes - Optional additional attributes to include in the log
 */

const logError = (error: unknown, identifier: string, extraAttributes?: LogErrorAttributes) => {
	const attributes: LogErrorAttributes = { identifier, ...extraAttributes }

	if (axios.isAxiosError(error)) {
		const ctx = getAxiosRequestContext(error)
		attributes.httpMethod = ctx.method
		attributes.httpErrorCode = ctx.status
		attributes.url = ctx.url
		attributes.fullUrl = ctx.fullUrl

		// Preserve old “headers.<k>” logging format (but sanitized)
		Object.entries(ctx.requestHeaders ?? {}).forEach(([key, value]) => {
			attributes[`headers.${key}`] = value
		})
	}

	Sentry.logger.error(`[${identifier}]: ${error instanceof Error ? error.message : String(error)}`, attributes)
	// NOTE: This is to have the error message in the console as well. Without prefixing with CONSOLE, the error will be logged twice to Sentry logs.
	// eslint-disable-next-line no-console
	console.error('CONSOLE', `[${identifier}]: ${error instanceof Error ? error.message : String(error)}`)
}

export default logError
